import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/client';
import { Users, Calendar, ClipboardList, FileText, ChevronRight, Plus, Bell, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const STATUT_COLORS = {
  'planifié': 'bg-blue-100 text-blue-700',
  'confirmé': 'bg-emerald-100 text-emerald-700',
  'annulé': 'bg-red-100 text-red-700',
  'terminé': 'bg-stone-100 text-stone-600',
};

const PRIORITE_CONFIG = {
  urgent: { color: 'text-red-600 bg-red-50 border-red-200', icon: AlertCircle, label: 'Urgent' },
  normal: { color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Clock, label: 'Normal' },
  faible: { color: 'text-stone-500 bg-stone-50 border-stone-200', icon: Bell, label: 'Faible' },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      API.get('/dashboard/stats'),
      API.get('/reminders', { params: { statut: 'actif' } }),
    ]).then(([r, rem]) => {
      setStats(r.data);
      setReminders(rem.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleDoneReminder = async (id) => {
    await API.put(`/reminders/${id}`, { statut: 'terminé' });
    setReminders(prev => prev.filter(r => r.id !== id));
  };

  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
  };

  const statCards = stats ? [
    { label: 'Patients', value: stats.total_patients, icon: Users, color: 'emerald', action: () => navigate('/patients') },
    { label: "RDV aujourd'hui", value: stats.appointments_today, icon: Calendar, color: 'blue', action: () => navigate('/appointments') },
    { label: 'Consultations', value: stats.consultations_total, icon: ClipboardList, color: 'amber', action: () => navigate('/consultations/new') },
    { label: 'Ordonnances', value: stats.prescriptions_total, icon: FileText, color: 'purple', action: () => navigate('/prescriptions') },
  ] : [];

  // Sort reminders: urgent first, then by date
  const today = new Date().toISOString().split('T')[0];
  const overdueReminders = reminders.filter(r => r.date_rappel < today);
  const todayReminders = reminders.filter(r => r.date_rappel === today);
  const upcomingReminders = reminders.filter(r => r.date_rappel > today).slice(0, 3);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900">Tableau de Bord</h1>
          <p className="text-stone-500 text-sm mt-1">
            {new Date().toLocaleDateString('fr-MA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="new-appointment-btn"
            onClick={() => navigate('/appointments')}
            className="flex items-center gap-1.5 px-4 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg hover:bg-stone-50 text-sm font-medium transition-colors"
          >
            <Calendar size={16} />
            Nouveau RDV
          </button>
          <button
            data-testid="new-consultation-btn"
            onClick={() => navigate('/consultations/new')}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-colors"
          >
            <Plus size={16} />
            Consultation
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon, color, action }) => (
          <div
            key={label}
            onClick={action}
            data-testid={`stat-card-${label.replace(/\s+/g, '-').toLowerCase()}`}
            className="bg-white border border-stone-200 rounded-xl p-5 cursor-pointer hover:shadow-md transition-all duration-200 group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${colorMap[color]}`}>
                <Icon size={20} />
              </div>
              <ChevronRight size={16} className="text-stone-300 group-hover:text-stone-500 transition-colors" />
            </div>
            <p className="text-2xl font-heading font-bold text-stone-900">{value}</p>
            <p className="text-sm text-stone-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent patients */}
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
            <h3 className="font-heading font-semibold text-stone-800">Derniers Patients</h3>
            <button onClick={() => navigate('/patients')} className="text-emerald-600 text-sm hover:text-emerald-700 font-medium">
              Voir tous
            </button>
          </div>
          <div className="divide-y divide-stone-50">
            {stats?.recent_patients?.length === 0 && (
              <p className="text-center text-stone-400 py-6 text-sm">Aucun patient</p>
            )}
            {stats?.recent_patients?.map(p => (
              <div
                key={p.id}
                onClick={() => navigate(`/patients/${p.id}`)}
                data-testid="patient-row"
                className="flex items-center gap-3 px-5 py-3.5 hover:bg-stone-50 cursor-pointer transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-700 font-semibold text-sm">
                    {p.prenom?.[0]}{p.nom?.[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800">{p.prenom} {p.nom}</p>
                  <p className="text-xs text-stone-500">{p.ville || 'Non renseigné'} · {p.telephone || '—'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming appointments */}
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
            <h3 className="font-heading font-semibold text-stone-800">Prochains Rendez-vous</h3>
            <button onClick={() => navigate('/appointments')} className="text-emerald-600 text-sm hover:text-emerald-700 font-medium">
              Voir tous
            </button>
          </div>
          <div className="divide-y divide-stone-50">
            {stats?.upcoming_appointments?.length === 0 && (
              <p className="text-center text-stone-400 py-6 text-sm">Aucun rendez-vous</p>
            )}
            {stats?.upcoming_appointments?.map(a => (
              <div key={a.id} data-testid="appointment-row" className="flex items-center gap-3 px-5 py-3.5">
                <div className="flex-shrink-0 text-center w-12">
                  <p className="text-base font-bold text-emerald-600 leading-tight">{a.heure}</p>
                  <p className="text-xs text-stone-400">{a.date?.slice(5)?.replace('-', '/')}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800">{a.patient_nom || 'Patient inconnu'}</p>
                  <p className="text-xs text-stone-500 truncate">{a.motif}</p>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUT_COLORS[a.statut] || 'bg-stone-100 text-stone-600'}`}>
                  {a.statut}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Reminders widget */}
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid="reminders-widget">
          <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-semibold text-stone-800">Rappels</h3>
              {(overdueReminders.length > 0 || todayReminders.length > 0) && (
                <span className="text-xs font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5">
                  {overdueReminders.length + todayReminders.length}
                </span>
              )}
            </div>
            <button onClick={() => navigate('/patients')} className="text-emerald-600 text-sm hover:text-emerald-700 font-medium">
              Patients
            </button>
          </div>
          <div className="divide-y divide-stone-50 max-h-64 overflow-y-auto">
            {reminders.length === 0 && (
              <p className="text-center text-stone-400 py-6 text-sm">
                <Bell size={24} className="text-stone-200 mx-auto mb-2" />
                Aucun rappel actif
              </p>
            )}
            {/* Overdue */}
            {overdueReminders.map(r => {
              const cfg = PRIORITE_CONFIG[r.priorite] || PRIORITE_CONFIG.normal;
              const Icon = cfg.icon;
              return (
                <div key={r.id} data-testid="reminder-row" className="flex items-start gap-3 px-4 py-3 bg-red-50/50">
                  <Icon size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{r.titre}</p>
                    <p className="text-xs text-red-500">{r.patient_nom} · En retard ({r.date_rappel})</p>
                  </div>
                  <button onClick={() => handleDoneReminder(r.id)} className="p-1 text-stone-300 hover:text-emerald-600 flex-shrink-0" title="Marquer fait">
                    <CheckCircle size={14} />
                  </button>
                </div>
              );
            })}
            {/* Today */}
            {todayReminders.map(r => {
              const cfg = PRIORITE_CONFIG[r.priorite] || PRIORITE_CONFIG.normal;
              const Icon = cfg.icon;
              return (
                <div key={r.id} data-testid="reminder-row" className="flex items-start gap-3 px-4 py-3">
                  <Icon size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{r.titre}</p>
                    <p className="text-xs text-stone-500">{r.patient_nom} · Aujourd'hui {r.heure_rappel || ''}</p>
                  </div>
                  <button onClick={() => handleDoneReminder(r.id)} className="p-1 text-stone-300 hover:text-emerald-600 flex-shrink-0" title="Marquer fait">
                    <CheckCircle size={14} />
                  </button>
                </div>
              );
            })}
            {/* Upcoming */}
            {upcomingReminders.map(r => {
              const cfg = PRIORITE_CONFIG[r.priorite] || PRIORITE_CONFIG.normal;
              const Icon = cfg.icon;
              return (
                <div key={r.id} data-testid="reminder-row" className="flex items-start gap-3 px-4 py-3 opacity-70">
                  <Icon size={14} className="text-stone-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-700 truncate">{r.titre}</p>
                    <p className="text-xs text-stone-400">{r.patient_nom} · {r.date_rappel}</p>
                  </div>
                  <button onClick={() => handleDoneReminder(r.id)} className="p-1 text-stone-300 hover:text-emerald-600 flex-shrink-0" title="Marquer fait">
                    <CheckCircle size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
